import { useState, useRef, useEffect, useCallback } from 'react';
import { suggestContacts } from '../../services/emailApi';

/**
 * Email address chip input with auto-complete from contacts.
 *
 * Comma, semicolon, Enter, or Tab converts typed text into a chip.
 * Typing 2+ chars triggers contact suggestions from the backend.
 */
export default function ChipInput({ value, onChange, placeholder, autoFocus }) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [focusedSuggestion, setFocusedSuggestion] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [autoFocus]);

  const [groupSuggestions, setGroupSuggestions] = useState([]);

  // Fetch suggestions on input change
  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) {
      setSuggestions([]);
      setGroupSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const data = await suggestContacts(q + '&current=' + encodeURIComponent(value.join(',')));
      const contacts = (data.contacts || []).filter((c) => !value.includes(c.email) && !value.includes(c.contact_email));
      const groups = (data.groups || []).filter(g => g.newMembers?.length > 0);
      setSuggestions(contacts);
      setGroupSuggestions(groups);
      setShowSuggestions(contacts.length > 0 || groups.length > 0);
      setFocusedSuggestion(-1);
    } catch {
      setSuggestions([]);
      setGroupSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value]);

  function addChip(text) {
    const email = text.trim().replace(/^<|>$/g, '');
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (!value.includes(email)) {
      onChange([...value, email]);
    }
    setInput('');
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function selectSuggestion(contact) {
    addChip(contact.email || contact.contact_email);
  }

  function handleKeyDown(e) {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedSuggestion((prev) => Math.min(prev + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedSuggestion((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' && focusedSuggestion >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[focusedSuggestion]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addChip(input);
    } else if (e.key === 'Tab') {
      // If there's text, convert to chip and stay. If empty, let Tab move to next field.
      if (input.trim()) {
        e.preventDefault();
        addChip(input);
      }
      // else: don't preventDefault — native Tab moves focus to next field
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function handleInputChange(e) {
    const val = e.target.value.replace(/[,;]$/, '');
    setInput(val);

    // Debounce suggestion fetch
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 200);
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const emails = text.split(/[,;\s]+/).filter(Boolean);
    const newAddrs = [...value];
    for (const em of emails) {
      const trimmed = em.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !newAddrs.includes(trimmed)) {
        newAddrs.push(trimmed);
      }
    }
    onChange(newAddrs);
  }

  function handleBlur() {
    // Delay to allow clicking suggestions
    setTimeout(() => {
      if (input.trim()) addChip(input);
      setShowSuggestions(false);
    }, 200);
  }

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative flex min-h-[28px] flex-wrap items-center gap-1">
      {value.map((email) => (
        <span key={email} className="flex items-center gap-0.5 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
          {email}
          <button
            type="button"
            onClick={() => onChange(value.filter((e) => e !== email))}
            className="ml-0.5 text-indigo-400 hover:text-indigo-700"
          >
            &times;
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        placeholder={value.length === 0 ? placeholder : ''}
        className="min-w-[100px] flex-1 border-0 py-0.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
      />

      {/* Suggestions dropdown */}
      {showSuggestions && (suggestions.length > 0 || groupSuggestions.length > 0) && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {/* Group suggestions first */}
          {groupSuggestions.map((group, i) => (
            <button
              key={`group-${i}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                // Add all new members from the group
                const newAddrs = group.newMembers.filter(m => !value.includes(m));
                if (newAddrs.length > 0) {
                  onChange([...value, ...newAddrs]);
                  setInput('');
                  setShowSuggestions(false);
                }
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 border-b border-gray-100"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">
                {group.members.length}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-blue-700">Add group ({group.newMembers.length} new)</p>
                <p className="truncate text-[11px] text-gray-500">{group.members.join(', ')}</p>
              </div>
              <span className="text-[10px] text-gray-300">{group.useCount}x</span>
            </button>
          ))}
          {/* Individual contacts */}
          {suggestions.map((contact, i) => {
            const email = contact.email || contact.contact_email;
            const name = contact.name || contact.contact_name;
            return (
              <button
                key={email}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(contact); }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  i === focusedSuggestion ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500">
                  {(email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  {name && <p className="truncate text-sm font-medium">{name}</p>}
                  <p className="truncate text-xs text-gray-500">{email}</p>
                </div>
                <span className="text-[10px] text-gray-300">{contact.use_count}x</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
